<template>
    <div class="file-manager">
        <div>
            <h5>Tender Files</h5>
        </div>
        <div v-if="isLoading" class="text-center py-3">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
        <ul v-else>
            <li v-for="file in files" :key="file.id" class="mt-2">
                <a :href="`/storage/${file.file_path}`" target="_blank">{{ file.file_name }}</a>
                <button @click="removeFile(file.id)" class="btn btn-sm btn-danger ms-2">Delete</button>
            </li>
        </ul>
        <div class="mt-3">
            <label class="custom-file-upload">
                <button type="button" class="btn btn-primary" @click="triggerFileInput">
                    Choose File
                </button>
                <input
                    ref="fileInput"
                    type="file"
                    @change="handleFileSelect"
                    :disabled="files.length >= 3"
                    hidden
                />
                <span class="file-name" v-if="selectedFileName">{{ selectedFileName }}</span>
                <span class="file-name" v-else>No file selected</span>
            </label>
            <small v-if="files.length >= 3" class="text-danger d-block mt-2">
                Maximum of 3 files.
            </small>
        </div>
        <div v-if="errorMessage" class="alert alert-danger mt-3">
            {{ errorMessage }}
        </div>
    </div>
</template>

<script>
import axios from "axios";

export default {
    props: {
        tenderId: {
            type: Number,
            required: false,
        },
        isEdit: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            files: [],
            errorMessage: "",
            selectedFileName: null,
            isLoading: false,
        };
    },
    methods: {
        fetchFiles() {
            if (this.isEdit && this.tenderId) {
                this.isLoading = true;
                axios
                    .get(`/api/tenders/${this.tenderId}/files`)
                    .then((response) => {
                        this.files = response.data.files || [];
                    })
                    .catch((error) => {
                        console.error("Failed to load files:", error);
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            }
        },
        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        handleFileSelect(event) {
            this.errorMessage = "";
            const fileInput = event.target;
            const file = fileInput.files[0];
            if (!file) return;

            this.selectedFileName = file.name;

            const formData = new FormData();
            formData.append("file", file);

            axios
                .post(`/api/tenders/${this.tenderId}/files`, formData)
                .then((response) => {
                    this.files.push(response.data.file);
                    this.resetFileInput();
                })
                .catch((error) => {
                    if (error.response) {
                        if (error.response.status === 413) {
                            this.errorMessage =
                                "The uploaded file is too large. Please check the file size.";
                        } else if (error.response.status === 422) {
                            const serverErrors = error.response.data.errors;
                            if (serverErrors && serverErrors.file) {
                                // Display detailed server error for file validation
                                this.errorMessage = serverErrors.file[0];
                            } else {
                                this.errorMessage =
                                    "You can upload a maximum of 3 files.";
                            }
                        } else {
                            this.errorMessage =
                                "An error occurred during the file upload.";
                        }
                    } else {
                        this.errorMessage =
                            "An unexpected error occurred. Please try again.";
                    }
                    this.resetFileInput(); // Reset file input if upload fails
                });
        },
        // Remove a file
        removeFile(fileId) {
            axios
                .delete(`/api/tenders/files/${fileId}`)
                .then(() => {
                    this.files = this.files.filter((file) => file.id !== fileId);
                })
                .catch((error) => {
                    console.error("Failed to delete the file:", error);
                });
        },
        // Reset the file input field
        resetFileInput() {
            const fileInput = this.$refs.fileInput;
            if (fileInput) {
                fileInput.value = ""; // Clear the file input
            }
            this.selectedFileName = null; // Clear selected file name
        },
    },
    watch: {
        tenderId: {
            immediate: true,
            handler(newTenderId) {
                this.errorMessage = "";
                this.resetFileInput();
                if (newTenderId) {
                    this.fetchFiles();
                }
            },
        },
    },
};
</script>

<style scoped>
.file-manager {
    border: 1px solid #ddd;
    padding: 15px;
    border-radius: 5px;
    background-color: #f9f9f9;
}
.file-manager ul {
    list-style-type: none;
    padding: 0;
}
.file-manager li {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
}
.file-manager li button {
    margin-left: 10px;
}
.custom-file-upload {
    display: flex;
    align-items: center;
    gap: 10px;
}
.custom-file-upload button {
    cursor: pointer;
}
.file-name {
    font-size: 0.9rem;
    color: #555;
}
.text-center {
    text-align: center;
}
</style>
